import { EXPENSE_MESSAGES } from "@/lib/expenses/rules";

/**
 * Department and Demand POs on a claim (spec R2 as reversed 2026-10-30, V3). Pure and shared by
 * the server and the form; the gateway repeats the type and year rules in its SQL.
 */

export interface ExpensePo {
  id: string;
  number: number | null;
  description: string | null;
  department: string;
  isDemand: boolean;
  /** The PO's year: a warehouse year period id. */
  periodId: string | null;
}

/** "#130 Amazon", and "#130 Amazon (Demand)" on a claim, where the type alone can't tell them. */
export function poLabel(
  number: number | null,
  description: string | null,
  isDemand = false,
): string | null {
  const parts = [number === null ? null : `#${number}`, description?.trim() || null];
  const label = parts.filter(Boolean).join(" ");
  if (label === "") return null;
  return isDemand ? `${label} (Demand)` : label;
}

/** The POs a claim in this year may pick, Demand POs first, plus the one it already has. */
export function offeredPos<
  P extends { id: string; periodId: string | null; isDemand: boolean; number: number | null },
>(pos: readonly P[], yearId: string | null, keptPoId: string | null): P[] {
  return pos
    .filter((p) => p.id === keptPoId || (yearId !== null && p.periodId === yearId))
    .sort((a, b) => Number(b.isDemand) - Number(a.isDemand) || (a.number ?? 0) - (b.number ?? 0));
}

/** What a claim already has: its PO, and whether the year being saved is the one it is in. */
export interface KeptPo {
  poId: string | null;
  sameYear: boolean;
}

/**
 * V3. Null when fine. An old claim may stay as it is, as in Vinyl: `kept` lets its own PO stand
 * only while the year is unchanged too. Moving year or changing PO always applies V3.
 */
export function poYearRefusal(
  po: { id: string; periodId: string | null } | null,
  yearId: string | null,
  kept: KeptPo | null = null,
): string | null {
  if (!po) return EXPENSE_MESSAGES.noPo;
  if (kept?.sameYear && po.id === kept.poId) return null;
  return yearId !== null && po.periodId === yearId ? null : EXPENSE_MESSAGES.poYear;
}
